import _ from 'lodash';
import { defineStore, storeToRefs } from 'pinia';
import { milliseconds } from 'date-fns';

import { HITL } from '@/api';
import { CleanUpSettings, SettingsObj, VoiceSettings } from '@/types';
import {
  SETTINGS_CATEGORIES,
  DEFAULT_CLEAN_UP_EXPIRE_IN_DAYS,
} from '@/constants';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';

export const useCommonSettingsStore = defineStore('HITL-Settings-Common', {
  state: () => {
    return {
      isReady: false,
      isUpdating: false,
      voice: { isEnabled: true } as VoiceSettings,
      cleanUp: {
        isEnabled: false,
        expireIn: milliseconds({ days: DEFAULT_CLEAN_UP_EXPIRE_IN_DAYS }),
      } as CleanUpSettings,
    };
  },
});

/** Agents can't update common settings? */
export const useCommonSettingsActions = () => {
  const store = useCommonSettingsStore();

  const initializeCommonSettings = async (options: {
    isCommon?: boolean;
  } = {}) => {
    try {
      if (store.isReady) {
        return;
      }

      store.isReady = false;
      const response = await HITL.settings.getSettings({
        isCommon: !!options.isCommon,
        categories: [
          SETTINGS_CATEGORIES.VOICE,
          SETTINGS_CATEGORIES.DATA_CLEANUP,
        ],
      });

      const voice = (response.data[SETTINGS_CATEGORIES.VOICE] as SettingsObj)?.data as VoiceSettings;
      if (!_.isEmpty(voice)) {
        store.voice = voice;
      }

      const cleanUp = (response.data[SETTINGS_CATEGORIES.DATA_CLEANUP] as SettingsObj)?.data as CleanUpSettings;
      if (!_.isEmpty(cleanUp)) {
        store.cleanUp = cleanUp;
      }

      store.isReady = true;
    } catch (error) {
      store.isReady = true;
      console.error('Error while initializing common settings!', error);
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const updateCommonSettings = async (data: {
    voice?: VoiceSettings;
    cleanUp?: CleanUpSettings;
  }) => {
    if (data.voice) {
      store.voice = data.voice;
    }
    if (data.cleanUp) {
      store.cleanUp = data.cleanUp;
    }
  };

  const updateAPICommonSettings = async (options: {
    isCommon?: boolean;
  } = {}) => {
    try {
      store.isUpdating = true;
      const request = {
        isCommon: !!options.isCommon,
        data: {
          [SETTINGS_CATEGORIES.VOICE]: store.voice,
          [SETTINGS_CATEGORIES.DATA_CLEANUP]: store.cleanUp,
        },
      };

      await HITL.settings.updateSettings(request);
      store.isUpdating = false;
    } catch (error) {
      store.isUpdating = false;
      console.error('Error while updating common settings!', error);
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  return {
    initializeCommonSettings,
    updateCommonSettings,
    updateAPICommonSettings,
  };
};

export const useCommonSettings = () => {
  return {
    ...storeToRefs(useCommonSettingsStore()),
    ...useCommonSettingsActions(),
  };
};
